import {
  handleIncomingErrorCode,
  queueCommands,
  resetCommandPipeline,
  tryResolvePendingCommand,
  type CommandQueueState,
} from "lib/deviceDrivers/sessionFramework/protocol/commandQueue";
import {
  buildGetDateCommand,
  buildGetTimeCommand,
  buildSetDateCommand,
  buildSetTimeCommand,
  syncHardwareConfigCommands,
} from "./commands";
import { tryParseEidOutputFormat } from "lib/deviceDrivers/truTest/scp/device/eid";
import { parseScpFrame } from "lib/deviceDrivers/truTest/scp/device/frames";
import { createScpCommandQueue } from "lib/deviceDrivers/truTest/scp/device/commands";
import {
  EIDOutputFormat,
  FrameType,
  type ScpFrame,
} from "lib/deviceDrivers/truTest/scp/device/types";
import {
  DeviceMode,
  type Xrp2iDeviceEventHandler,
  type Xrp2iDeviceHandle,
  Xrp2iEventType,
} from "./types";
import {
  IRxTransport,
  ITxTransport,
} from "lib/deviceDrivers/sessionFramework/device/types";
import {
  processLiveScpFrame,
  safeEmit,
} from "lib/deviceDrivers/truTest/scp/device/liveFrame";

interface Xrp2iState {
  mode: DeviceMode;
  lineEndingsEnabled: boolean;
  eidOutputFormat?: EIDOutputFormat;
  /** System time captured when a time-sync sequence was initiated. */
  timeSyncInitiatedAt?: Date;
  /** Device date string received from {GD}, held until {GT} arrives. */
  deviceDateStr?: string | null;
}

/**
 * Creates a live XRP2i protocol device for one transport pair.
 * The returned handle owns command sequencing, retries, and teardown for that scanner.
 */
export function createXrp2iDevice(
  tx: ITxTransport,
  rx: IRxTransport,
  onEvent: Xrp2iDeviceEventHandler,
): Xrp2iDeviceHandle {
  const queue = createScpCommandQueue();
  const xrp2i: Xrp2iState = {
    mode: DeviceMode.IDLE,
    lineEndingsEnabled: false,
  };

  function onFatalFailure(code: string): void {
    safeEmit(onEvent, { type: Xrp2iEventType.ERROR, code });
    xrp2i.mode = DeviceMode.IDLE;
  }

  const unsubscribe = rx.onReceive(payload => {
    receive(queue, xrp2i, tx, payload, onEvent, onFatalFailure);
  });

  return {
    async resetHardwareConfig() {
      resetCommandPipeline(queue);
      xrp2i.mode = DeviceMode.AWAITING_LINE_ENDINGS_STATE;
      queueCommands(queue, tx, syncHardwareConfigCommands(), onFatalFailure);
    },

    setDeviceTime() {
      const now = new Date();
      xrp2i.timeSyncInitiatedAt = now;
      // If hardware config is already done, enter the date-query phase immediately.
      // If config is still running, the EID-format handler will transition us there
      // once config completes (since timeSyncInitiatedAt will already be set).
      if (xrp2i.mode === DeviceMode.IDLE) {
        xrp2i.mode = DeviceMode.AWAITING_DEVICE_DATE;
      }
      queueCommands(
        queue,
        tx,
        [
          buildGetDateCommand(),
          buildGetTimeCommand(),
          buildSetDateCommand(formatLocalDate(now)),
          buildSetTimeCommand(formatLocalTime(now)),
        ],
        onFatalFailure,
      );
    },

    destroy() {
      unsubscribe();
      resetCommandPipeline(queue);
      xrp2i.mode = DeviceMode.IDLE;
    },
  };
}

/**
 * Parses an incoming SCP frame and routes it to the right handler for the current device mode. Both the command acknowledgement step and the mode state machine run here, so live scan frames are only processed after the hardware setup has finished.
 */
function receive(
  queue: CommandQueueState<ScpFrame>,
  xrp2i: Xrp2iState,
  tx: ITxTransport,
  serializedPayload: string,
  onEvent: Xrp2iDeviceEventHandler,
  onFatalFailure: (code: string) => void,
): void {
  const payload = xrp2i.lineEndingsEnabled
    ? serializedPayload.replace(/\r\n$/, "")
    : serializedPayload;
  const frame = parseScpFrame(payload);

  if (frame.type === FrameType.ERROR) {
    handleIncomingErrorCode(queue, tx, frame.code, onFatalFailure);
    return;
  }

  tryResolvePendingCommand(queue, tx, frame, onFatalFailure);

  if (xrp2i.mode === DeviceMode.AWAITING_LINE_ENDINGS_STATE) {
    if (frame.type === FrameType.BRACKET) {
      xrp2i.lineEndingsEnabled = frame.value === "1";
      xrp2i.mode = DeviceMode.AWAITING_EID_OUTPUT_FORMAT;
    }
    return;
  }

  if (frame.type === FrameType.BRACKET) {
    const detectedFormat = tryParseEidOutputFormat(frame.raw);
    if (detectedFormat !== undefined) {
      xrp2i.eidOutputFormat = detectedFormat;
      if (xrp2i.mode === DeviceMode.AWAITING_EID_OUTPUT_FORMAT) {
        // Transition to date-query phase if a time sync is queued, otherwise idle.
        xrp2i.mode = xrp2i.timeSyncInitiatedAt
          ? DeviceMode.AWAITING_DEVICE_DATE
          : DeviceMode.IDLE;
      }
      return;
    }

    if (xrp2i.mode === DeviceMode.AWAITING_DEVICE_DATE) {
      xrp2i.deviceDateStr = frame.value;
      xrp2i.mode = DeviceMode.AWAITING_DEVICE_TIME;
      return;
    }

    if (xrp2i.mode === DeviceMode.AWAITING_DEVICE_TIME) {
      const deviceTime = tryParseDeviceDateTime(
        xrp2i.deviceDateStr,
        frame.value,
      );
      if (deviceTime && xrp2i.timeSyncInitiatedAt) {
        const driftMs =
          xrp2i.timeSyncInitiatedAt.getTime() - deviceTime.getTime();
        safeEmit(onEvent, { type: Xrp2iEventType.TIME_SYNCED, driftMs });
      }
      xrp2i.deviceDateStr = undefined;
      xrp2i.timeSyncInitiatedAt = undefined;
      xrp2i.mode = DeviceMode.IDLE;
      return;
    }
  }

  if (xrp2i.mode === DeviceMode.AWAITING_EID_OUTPUT_FORMAT) {
    xrp2i.mode = xrp2i.timeSyncInitiatedAt
      ? DeviceMode.AWAITING_DEVICE_DATE
      : DeviceMode.IDLE;
  }

  if (
    xrp2i.mode === DeviceMode.AWAITING_DEVICE_DATE ||
    xrp2i.mode === DeviceMode.AWAITING_DEVICE_TIME
  ) {
    return;
  }

  processLiveScpFrame(
    frame,
    xrp2i.eidOutputFormat,
    eid =>
      safeEmit(onEvent, {
        type: Xrp2iEventType.SCAN,
        eid,
        created: new Date().toISOString(),
      }),
    () => safeEmit(onEvent, { type: Xrp2iEventType.INVALID_EID }),
  );
}

/**
 * Reconstructs a local Date from the date and time strings returned by the device. Returns null if either string is missing or does not match the expected format.
 */
function tryParseDeviceDateTime(
  dateStr: string | null | undefined,
  timeStr: string | null | undefined,
): Date | null {
  if (!dateStr || !timeStr) {
    return null;
  }
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const timeMatch = /^(\d{2}):(\d{2}):(\d{2})$/.exec(timeStr);
  if (!dateMatch || !timeMatch) {
    return null;
  }
  return new Date(
    parseInt(dateMatch[1], 10),
    parseInt(dateMatch[2], 10) - 1,
    parseInt(dateMatch[3], 10),
    parseInt(timeMatch[1], 10),
    parseInt(timeMatch[2], 10),
    parseInt(timeMatch[3], 10),
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function formatLocalTime(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}
